import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import {
  useLandingPromoBackgroundJob,
  useLandingPromoBackgroundJobMeta,
  isLandingPromoBgJobBusy,
} from './utils/landingPromotionBackgroundJobStore';

const LANDING_PROMO_PATH = '/workspace/landing-promotion';

/** Subscribes to progress only — keeps sidebar / layout off the hot path */
function LandingPromoBannerProgressSection() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const job = useLandingPromoBackgroundJob();
  if (!isLandingPromoBgJobBusy(job)) return null;
  const isUpload = job.status === 'uploading';
  const onLandingPage =
    pathname === LANDING_PROMO_PATH || pathname.endsWith('/landing-promotion');

  return (
    <div className="flex shrink-0 flex-col items-stretch gap-2 sm:w-64">
      <div className="flex items-center justify-between gap-2 text-xs text-slate-300">
        <span className="truncate">
          {isUpload
            ? t('backoffice.landing_promotion_bg_banner_upload', { defaultValue: 'Uploading…' })
            : `${job.progress}%`}
        </span>
        {!onLandingPage ? (
          <Link
            to={LANDING_PROMO_PATH}
            className="font-medium text-white underline decoration-slate-400 underline-offset-2 hover:decoration-white"
          >
            {t('backoffice.landing_promotion_bg_banner_link', {
              defaultValue: 'Landing promotion',
            })}
          </Link>
        ) : null}
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-700">
        <div
          className="h-full rounded-full bg-emerald-400 transition-[width] duration-200 ease-out"
          style={{ width: isUpload ? '12%' : `${job.progress}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Fixed notice while a server-side hero image job runs (upload / background removal).
 * Outer shell uses meta subscription only so workspace navigation stays smooth.
 */
export default function LandingPromoBackgroundJobBanner() {
  const { t } = useTranslation();
  const meta = useLandingPromoBackgroundJobMeta();
  const busy = isLandingPromoBgJobBusy(meta);

  React.useEffect(() => {
    if (!busy) {
      document.documentElement.style.removeProperty('--landing-promo-job-pad');
      return;
    }
    document.documentElement.style.setProperty('--landing-promo-job-pad', '6rem');
    return () => {
      document.documentElement.style.removeProperty('--landing-promo-job-pad');
    };
  }, [busy]);

  if (!busy) return null;

  return (
    <div
      className="pointer-events-auto fixed bottom-0 left-0 right-0 z-[80] border-t border-slate-200 bg-slate-900 px-4 py-3 text-white shadow-[0_-8px_30px_rgba(0,0,0,0.12)] md:left-52"
      style={{ contain: 'layout' }}
      role="status"
      aria-busy="true"
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">
            {t('backoffice.landing_promotion_bg_banner_title', {
              defaultValue: 'Hero image — removing background on server…',
            })}
          </p>
          <p className="mt-0.5 text-xs text-slate-300">
            {t('backoffice.landing_promotion_bg_banner_hint', {
              defaultValue:
                'You can close this tab or use other workspace pages. Processing continues on the server.',
            })}
          </p>
        </div>
        <LandingPromoBannerProgressSection />
      </div>
    </div>
  );
}
